'use client';

import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Search, SlidersHorizontal } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { cn } from '@/doi-tac-module/lib/utils';
import { industriesByJobType, type Industry, allIndustries } from "@/doi-tac-module/lib/industry-data";
import { visaDetailsByVisaType } from '@/doi-tac-module/lib/visa-data';
import { type SearchFilters, getIndustryKeyByLaborType } from '@/doi-tac-module/lib/candidate-filter-utils';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';

type CandidateSearchBannerProps = {
    filters: SearchFilters;
    onFilterChange: (newFilters: Partial<SearchFilters>) => void;
    onSearch: () => void;
    isAgency?: boolean;
};

export const CandidateSearchBanner = ({ filters, onFilterChange, onSearch, isAgency = false }: CandidateSearchBannerProps) => {
    const { language } = useLanguage();
    const t = agencyTranslations.candidateSearch;
    const [availableIndustries, setAvailableIndustries] = useState<Industry[]>(allIndustries);
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);

    useEffect(() => {
        const parentVisaSlug = getIndustryKeyByLaborType(filters.visa) || Object.keys(industriesByJobType).find(key =>
            (visaDetailsByVisaType[key as keyof typeof visaDetailsByVisaType] || []).some(detail => detail.slug === filters.visaDetail)
        );

        const industries = parentVisaSlug ? (industriesByJobType[parentVisaSlug as keyof typeof industriesByJobType] || allIndustries) : allIndustries;
        const uniqueIndustries = Array.from(new Map(industries.map(item => [item.name, item])).values());
        setAvailableIndustries(uniqueIndustries);
    }, [filters.visa, filters.visaDetail]);

    const handleJobTypeChange = (value: string) => {
        const newVisa = value === 'all' ? '' : value;
        const newFilters: Partial<SearchFilters> = { visa: newVisa, visaDetail: '' };
        
        if (filters.industry && filters.industry !== 'all') {
            const visaKey = getIndustryKeyByLaborType(newVisa);
            const newIndustries = visaKey ? (industriesByJobType[visaKey] || allIndustries) : allIndustries;
            const isStillValid = newIndustries.some(ind => ind.name === filters.industry);
            if (!isStillValid) {
                newFilters.industry = '';
                newFilters.jobDetail = '';
            }
        }
        
        onFilterChange(newFilters);
    };

    const handleSearchClick = () => {
        onSearch();
        if (window.innerWidth < 768) {
            setIsSearchExpanded(false);
        }
    };

    const searchSummary = useMemo(() => {
        const parts = [];
        if (filters.visa) {
            const visaKey = filters.visa === 'Lao động phổ thông' ? 'unskilled' :
                            filters.visa === 'Lao động kỹ thuật' ? 'skilled' :
                            filters.visa === 'Kỹ sư, chuyên gia' ? 'engineer' : null;
            parts.push(visaKey ? t.banner.laborTypes[visaKey][language] : filters.visa);
        }
        if (filters.industry) {
            const industryName = agencyTranslations.candidateSearch.industryNames[filters.industry]?.[language] || filters.industry;
            parts.push(industryName);
        }
        if (filters.desiredSalaryVietnam) {
            const salaryText = t.banner.placeholders.salaryUnder[language].replace('{val}', Number(filters.desiredSalaryVietnam).toLocaleString(language === 'vi' ? 'vi-VN' : 'en-US'));
            parts.push(salaryText);
        }
        return parts.join(' / ');
    }, [filters, language, t]);

    return (
        <section className={cn(
            "w-full transition-all duration-500 py-4",
            isAgency 
                ? "bg-gradient-to-br from-[#FF6921] to-[#F2B92A] text-white" 
                : "bg-gradient-to-r from-blue-600 to-sky-500 text-white"
        )}>
            <div className={cn("container mx-auto px-4 max-w-screen-2xl relative z-10")}>
                <Card className="max-w-[70rem] mx-auto shadow-2xl overflow-visible border-none">
                    <div className="lg:hidden p-2">
                        <Button 
                            variant="ghost" 
                            className="w-full justify-start text-left h-auto py-3 bg-white hover:bg-slate-50 transition-colors rounded-xl"
                            onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                        >
                            <SlidersHorizontal className="h-5 w-5 mr-3 text-slate-400"/>
                            <div className="flex-grow min-w-0">
                                <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-0.5">
                                    {agencyTranslations.common.filteringBy[language]}
                                </p>
                                <p className="font-bold text-slate-700 truncate text-sm">
                                    {searchSummary || agencyTranslations.common.all[language]}
                                </p>
                            </div>
                        </Button>
                    </div>

                    <div id="TIMKIEM_BANNER" className={cn(
                        "p-4 md:p-6",
                        isSearchExpanded ? "block" : "hidden md:block"
                    )}>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 items-end">
                            <div className="space-y-2">
                                <Label htmlFor="search-type" className="text-foreground text-sm font-semibold">
                                    {t.banner.labels.laborType[language]}
                                </Label>

                                <Select onValueChange={(value) => handleJobTypeChange(value)} value={filters.visa || 'all'}>
                                    <SelectTrigger id="search-type" className="h-11 rounded-xl border-slate-200 bg-white shadow-sm">
                                        <SelectValue placeholder={t.banner.placeholders.allLaborTypes[language]} />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl border-slate-100 shadow-xl">
                                        <SelectItem value="all">{t.banner.placeholders.allLaborTypes[language]}</SelectItem>
                                        <SelectItem value="Lao động phổ thông">{t.banner.laborTypes.unskilled[language]}</SelectItem>
                                        <SelectItem value="Lao động kỹ thuật">{t.banner.laborTypes.skilled[language]}</SelectItem>
                                        <SelectItem value="Kỹ sư, chuyên gia">{t.banner.laborTypes.engineer[language]}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="search-industry" className="text-foreground text-sm font-semibold">
                                    {t.banner.labels.industry[language]}
                                </Label>
                                <Select onValueChange={(value) => onFilterChange({ industry: value === 'all' ? '' : value })} value={filters.industry || 'all'}>
                                    <SelectTrigger id="search-industry" className="h-11 rounded-xl border-slate-200 bg-white shadow-sm">
                                        <SelectValue placeholder={t.banner.placeholders.allIndustries[language]} />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl border-slate-100 shadow-xl">
                                        <SelectItem value="all">{t.banner.placeholders.allIndustries[language]}</SelectItem>
                                        {availableIndustries.map((industry) => (
                                            <SelectItem key={industry.slug} value={industry.name}>
                                                {t.industryNames[industry.name]?.[language] || industry.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="search-salary" className="text-foreground text-sm font-semibold">
                                    {t.banner.labels.expectedSalary[language]}
                                </Label>
                                <Select
                                    onValueChange={(value) => onFilterChange({ desiredSalaryVietnam: value === 'all' ? '' : value })}
                                    value={
                                        ['400', '500', '600', '700', '800', '900', '1000', '1200', '1500', '2000', '2500', '3000', '4000', '5000', '10000', '20000'].includes(filters.desiredSalaryVietnam || '')
                                            ? filters.desiredSalaryVietnam
                                            : 'all'
                                    }
                                >
                                    <SelectTrigger id="search-salary" className="h-11 rounded-xl border-slate-200 bg-white shadow-sm">
                                        <SelectValue placeholder={t.banner.placeholders.allSalaries[language]} />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl border-slate-100 shadow-xl max-h-[300px]">
                                        <SelectItem value="all">{t.banner.placeholders.allSalaries[language]}</SelectItem>
                                        {['400', '500', '600', '700', '800', '900', '1000', '1200', '1500', '2000', '2500', '3000', '4000', '5000', '10000', '20000'].map(val => (
                                            <SelectItem key={val} value={val}>
                                                {t.banner.placeholders.salaryUnder[language].replace('{val}', Number(val).toLocaleString(language === 'vi' ? 'vi-VN' : 'en-US'))}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2 text-center md:text-right">
                                <Button size="lg" className={cn(
                                    "w-full text-white text-lg h-11 rounded-xl font-medium border-none shadow-lg",
                                    isAgency 
                                        ? "bg-[#FF6921] hover:bg-[#FF6921]/90 shadow-orange-500/20" 
                                        : "bg-primary hover:bg-primary/90 shadow-blue-500/20"
                                )} onClick={handleSearchClick}>
                                    <Search className="mr-2 h-5 w-5" /> {t.banner.searchButton[language]}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        </section>
    );
};
